import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer,
  Tooltip as ChartTooltip, XAxis, YAxis,
} from "recharts";
import { ArrowLeftRight } from "lucide-react";
import {
  formatCount, formatMoney, formatMoneyShort, getMovements, metricsUrls, monthLabel,
  type MovementsResponse, type Segment,
} from "@/lib/metrics";
import { EmptyState, ErrorState, TableSkeleton } from "./shared";

const POSITIVE = [
  { key: "new", label: "New", color: "#2563eb" },
  { key: "expansion", label: "Expansion", color: "#16a34a" },
  { key: "reactivation", label: "Reactivation", color: "#0ea5e9" },
] as const;

const NEGATIVE = [
  { key: "contraction", label: "Contraction", color: "#f59e0b" },
  { key: "churn", label: "Churn", color: "#dc2626" },
] as const;

export default function MovementsTab({
  segment, months, onChangeMonths,
}: {
  segment: Segment;
  months: number;
  onChangeMonths: (months: number) => void;
}) {
  const params = { ...segment, months };
  const { data, isLoading, error } = useQuery<MovementsResponse>({
    queryKey: [metricsUrls.movements(params)],
    queryFn: () => getMovements(params),
  });

  const rows = data?.rows || [];

  const chartData = useMemo(() => rows.map((r) => ({
    month: monthLabel(r.month),
    raw: r.month,
    new: Math.abs(r.new),
    expansion: Math.abs(r.expansion),
    reactivation: Math.abs(r.reactivation),
    contraction: -Math.abs(r.contraction),
    churn: -Math.abs(r.churn),
    netNew: r.netNew,
  })), [rows]);

  return (
    <div className="space-y-6" data-testid="tab-movements">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><ArrowLeftRight className="w-4 h-4" />MRR movements by month</CardTitle>
              <CardDescription>Where every dollar of MRR came from and where it went. Above the line is money gained, below is money lost, and the line is the net.</CardDescription>
            </div>
            <Tabs value={String(months)} onValueChange={(v) => onChangeMonths(Number(v))}>
              <TabsList className="h-9">
                <TabsTrigger value="6" data-testid="movements-6">6 months</TabsTrigger>
                <TabsTrigger value="12" data-testid="movements-12">12 months</TabsTrigger>
                <TabsTrigger value="24" data-testid="movements-24">24 months</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? <TableSkeleton rows={5} cols={6} />
            : error ? <ErrorState error={error} />
              : rows.length === 0 ? (
                <EmptyState title="No movements recorded" hint="The MRR ledger has no events yet. Run a full sync from the header, then come back." />
              ) : (
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }} stackOffset="sign">
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} width={70} tickFormatter={(v: number) => formatMoneyShort(v)} />
                      <ChartTooltip formatter={(v: any, n: any) => [formatMoney(Number(v)), n]} />
                      <Legend />
                      <ReferenceLine y={0} stroke="hsl(var(--border))" />
                      {POSITIVE.map((s) => <Bar key={s.key} dataKey={s.key} name={s.label} stackId="mrr" fill={s.color} isAnimationActive={false} />)}
                      {NEGATIVE.map((s) => <Bar key={s.key} dataKey={s.key} name={s.label} stackId="mrr" fill={s.color} isAnimationActive={false} />)}
                      <Line type="monotone" dataKey="netNew" name="Net new MRR" stroke="hsl(var(--foreground))" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">The numbers</CardTitle>
          <CardDescription>Opening MRR, each movement, closing MRR, and the customer counts behind them.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? <TableSkeleton rows={6} cols={8} />
            : rows.length === 0 ? null : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">MRR start</TableHead>
                      <TableHead className="text-right">New</TableHead>
                      <TableHead className="text-right">Expansion</TableHead>
                      <TableHead className="text-right">Reactivation</TableHead>
                      <TableHead className="text-right">Contraction</TableHead>
                      <TableHead className="text-right">Churn</TableHead>
                      <TableHead className="text-right">Net new</TableHead>
                      <TableHead className="text-right">MRR end</TableHead>
                      <TableHead className="text-right">New cust.</TableHead>
                      <TableHead className="text-right">Reactivated</TableHead>
                      <TableHead className="text-right">Upgraded</TableHead>
                      <TableHead className="text-right">Downgraded</TableHead>
                      <TableHead className="text-right">Cancelled</TableHead>
                      <TableHead className="text-right">Customers end</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.month} data-testid={`movements-row-${r.month}`}>
                        <TableCell className="whitespace-nowrap font-medium">{monthLabel(r.month)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatMoney(r.mrrStart)}</TableCell>
                        <TableCell className="text-right tabular-nums text-blue-600 dark:text-blue-400">{formatMoney(Math.abs(r.new))}</TableCell>
                        <TableCell className="text-right tabular-nums text-emerald-600 dark:text-emerald-400">{formatMoney(Math.abs(r.expansion))}</TableCell>
                        <TableCell className="text-right tabular-nums text-sky-600 dark:text-sky-400">{formatMoney(Math.abs(r.reactivation))}</TableCell>
                        <TableCell className="text-right tabular-nums text-amber-600 dark:text-amber-400">−{formatMoney(Math.abs(r.contraction))}</TableCell>
                        <TableCell className="text-right tabular-nums text-destructive">−{formatMoney(Math.abs(r.churn))}</TableCell>
                        <TableCell className={`text-right tabular-nums font-medium ${r.netNew < 0 ? "text-destructive" : ""}`}>{formatMoney(r.netNew)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{formatMoney(r.mrrEnd)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCount(r.newCustomers)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCount(r.reactivated)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCount(r.upgraded)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCount(r.downgraded)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCount(r.cancelled)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{formatCount(r.customersEnd)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
